// src/components/OrderCard.js

import React from 'react';
import t, { locale } from '../i18n';

// ── Вспомогательные функции ───────────────────────────────────────────────────

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso + 'Z');
    return d.toLocaleString(locale, {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        timeZone: 'UTC', timeZoneName: 'short',
    });
}

function formatHuman(value) {
    if (value === null || value === undefined) return '—';
    const num = parseFloat(value);
    if (isNaN(num)) return '—';
    const str = num.toString();
    if (!str.includes('.')) return str + '.0';
    const trimmed = str.replace(/\.?0+$/, '');
    return trimmed.includes('.') ? trimmed : trimmed + '.0';
}

// Разбивает текст на куски по N символов с <wbr> между ними.
// Используем span+onClick вместо <a> чтобы <wbr> не ломал href.
function HashLink({ text, href, style }) {
    if (!text) return <span style={style}>—</span>;
    const chunks = text.match(/.{1,10}/g) || [text];
    const handleClick = () => href && window.open(href, '_blank', 'noopener,noreferrer');
    return (
        <span
            style={{ ...style, cursor: href ? 'pointer' : 'default' }}
            onClick={handleClick}
            title={text}
        >
            {chunks.map((chunk, i) => (
                <span key={i}>{chunk}{i < chunks.length - 1 && <wbr />}</span>
            ))}
        </span>
    );
}

// Внутренняя ссылка (react-router navigate)
function HashInternalLink({ text, to, style }) {
    if (!text) return <span style={style}>—</span>;
    const chunks = text.match(/.{1,10}/g) || [text];
    const handleClick = () => to && (window.location.href = to);
    return (
        <span
            style={{ ...style, cursor: 'pointer' }}
            onClick={handleClick}
            title={text}
        >
            {chunks.map((chunk, i) => (
                <span key={i}>{chunk}{i < chunks.length - 1 && <wbr />}</span>
            ))}
        </span>
    );
}

function statusColor(tableSource) {
    if (!tableSource) return '#888';
    if (tableSource.includes('pending'))   return '#f0a500';
    if (tableSource.includes('completed')) return '#28a745';
    if (tableSource.includes('cancel'))    return '#dc3545';
    if (tableSource === 'transactions')    return '#2196f3';
    return '#888';
}

const PAYABLE_CURRENCIES = ['EUR', 'USD', 'RUB'];
function canPay(order) {
    if (order.table_source !== 'orders_add_pending') return false;
    return PAYABLE_CURRENCIES.includes(order.currency?.currency_iso);
}

// ── Строка детали ─────────────────────────────────────────────────────────────

function Row({ label, children }) {
    if (!children) return null;
    return (
        <div style={s.row}>
            <span style={s.label}>{label}</span>
            <span style={s.value}>{children}</span>
        </div>
    );
}

// ── Главный компонент ─────────────────────────────────────────────────────────

export default function OrderCard({ order, onPay }) {
    if (!order) return null;

    const c        = order.currency || {};
    const refText  = order.order_ref || order.ref;

    return (
        <div style={s.card}>

            {/* Строка 1: статус слева, дата справа */}
            <div style={s.topRow}>
                <span style={{ ...s.badge, backgroundColor: statusColor(order.table_source) }}>
                    {order.type || order.table_source}
                </span>
                <span style={s.dateText}>
                    {formatDate(order.date)}
                </span>
            </div>

            {/* Ref по центру — HashLink с <wbr> каждые 10 символов */}
            <div style={s.refBlock}>
                <HashLink
                    text={refText}
                    href={order.order_url}
                    style={s.refLink}
                />
            </div>

            {/* Блок суммы */}
            <div style={s.amountBlock}>
                <span style={s.currencyMark}>{c.currency_symbol}</span>
                <span style={s.amountNum}>{formatHuman(order.amount_human)}</span>
                <span style={s.currencyMark}>{c.currency_name}</span>
            </div>

            {/* Детали */}
            <div style={s.details}>

                <Row label={t.sender}>
                    <HashLink
                        text={order.resp_get?.account}
                        style={s.mono}
                    />
                </Row>

                <Row label={t.receiver}>
                    <HashLink
                        text={order.resp_send?.account}
                        style={s.mono}
                    />
                </Row>

                {order.order_ref && (
                    <Row label={t.linkedOrder}>
                        <HashInternalLink
                            text={order.order_ref}
                            to={`/${order.order_ref}`}
                            style={s.monoLink}
                        />
                    </Row>
                )}

                {order.description && (
                    <Row label={t.description}>{order.description}</Row>
                )}

                {order.fee_send_human > 0 && (
                    <Row label={t.feeSend}>
                        <span style={s.fee}>
                            {c.currency_symbol}{formatHuman(order.fee_send_human)} {c.currency_name}
                        </span>
                    </Row>
                )}
                {order.fee_get_human > 0 && (
                    <Row label={t.feeGet}>
                        <span style={s.fee}>
                            {c.currency_symbol}{formatHuman(order.fee_get_human)} {c.currency_name}
                        </span>
                    </Row>
                )}

            </div>

            {canPay(order) && (
                <button onClick={onPay} style={s.payButton}>{t.pay}</button>
            )}
        </div>
    );
}

// ── Стили ─────────────────────────────────────────────────────────────────────

const s = {
    card: {
        backgroundColor: '#1e1e1e',
        border:          '1px solid #2a2a2a',
        borderRadius:    '12px',
        padding:         '20px 20px 24px',
        width:           '100%',
        maxWidth:        '560px',
        margin:          '24px auto 0',
        boxSizing:       'border-box',
        textAlign:       'left',
    },
    topRow: {
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        flexWrap:       'wrap',
        gap:            '8px',
        marginBottom:   '10px',
    },
    badge: {
        display:      'inline-block',
        padding:      '4px 14px',
        borderRadius: '20px',
        fontSize:     '13px',
        fontWeight:   600,
        color:        '#fff',
        flexShrink:   0,
    },
    dateText: {
        fontSize:   '12px',
        color:      '#666',
        textAlign:  'right',
        flexShrink: 0,
    },
    refBlock: {
        textAlign:    'center',
        marginBottom: '28px',
        marginTop:    '6px',
        padding:      '0 4px',
        lineHeight:   1.6,
    },
    refLink: {
        color:      '#5c35cc',
        fontFamily: 'monospace',
        fontSize:   '11px',
    },
    amountBlock: {
        textAlign:    'center',
        marginBottom: '28px',
        lineHeight:   1,
    },
    currencyMark: {
        fontSize:   '16px',
        fontWeight: 400,
        color:      '#888',
        margin:     '0 4px',
    },
    amountNum: {
        fontSize:   '32px',
        fontWeight: 700,
        color:      '#fff',
        margin:     '0 2px',
    },
    details: {
        display:       'flex',
        flexDirection: 'column',
        gap:           '12px',
    },
    row: {
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'flex-start',
        gap:            '12px',
        borderBottom:   '1px solid #252525',
        paddingBottom:  '12px',
    },
    label: {
        fontSize:      '11px',
        color:         '#555',
        flexShrink:    0,
        paddingTop:    '2px',
        minWidth:      '110px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    value: {
        fontSize:  '13px',
        color:     '#ccc',
        textAlign: 'right',
    },
    mono: {
        fontFamily: 'monospace',
        fontSize:   '12px',
        color:      '#bbb',
        textAlign:  'right',
        lineHeight: 1.6,
    },
    monoLink: {
        fontFamily: 'monospace',
        fontSize:   '12px',
        color:      '#5c35cc',
        textAlign:  'right',
        lineHeight: 1.6,
    },
    fee: {
        fontSize: '13px',
        color:    '#f0a500',
    },
    payButton: {
        width:           '100%',
        padding:         '14px',
        fontSize:        '16px',
        fontWeight:      600,
        backgroundColor: '#6200ea',
        color:           '#fff',
        border:          'none',
        borderRadius:    '8px',
        cursor:          'pointer',
        marginTop:       '20px',
    },
};
